import { Check, Copy, Landmark } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";

export interface PsePaymentVoucherProps {
  /** Already formatted for display — this component never does money arithmetic. `null` before pricing. */
  amount: string | null;
  currency: string;
  /** Short operation reference, shown here because it is what the payer will look for in their statement. */
  reference: string;
}

/**
 * The two facts the payer needs while filling the form: what they are about to be debited, and the reference
 * that will identify it afterwards.
 *
 * Shaped as a teller's voucher stub — figure above, perforation, reference below — because that is the
 * artifact this flow produces: PSE is an authorization at the payer's own bank, and the reference is the
 * string they will quote when they call about it. The reference is a copy button rather than static text for
 * the same reason.
 */
export function PsePaymentVoucher({ amount, currency, reference }: PsePaymentVoucherProps) {
  const { t } = useTranslations("payins");
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    },
    [],
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(reference);
      setCopied(true);
      if (resetTimer.current) clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (permissions, insecure context) — the reference is still readable on the button.
    }
  };

  return (
    <ColocaSurface
      variant="plain"
      padding="none"
      className="overflow-hidden border-primary/30 bg-primary/[0.04]"
    >
      <div className="flex items-start justify-between gap-4 px-4 pt-3.5 pb-3">
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/75">
            {t("pse_voucher_amount_label")}
          </p>
          <p className="mt-1.5 flex items-baseline gap-1.5 font-mono text-[26px] leading-none font-bold tracking-tight tabular-nums text-foreground">
            <span className="truncate">{amount ?? "—"}</span>
            <span className="text-xs font-medium tracking-wider text-muted-foreground">{currency}</span>
          </p>
        </div>
        <Landmark aria-hidden="true" className="mt-0.5 size-7 shrink-0 text-primary/25" />
      </div>

      {/* Perforation — the one decorative line, and it earns its place by splitting the figure from its id. */}
      <div aria-hidden="true" className="border-t border-dashed border-primary/30" />

      <div className="flex items-center justify-between gap-3 py-1.5 pr-1.5 pl-4">
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
          {t("pse_voucher_reference_label")}
        </span>
        <ColocaButton
          type="button"
          variant="ghost"
          size="xs"
          icon={copied ? Check : Copy}
          onClick={handleCopy}
          aria-label={copied ? t("pse_voucher_copied") : t("pse_voucher_copy")}
          className="font-mono tracking-wider"
        >
          {reference}
        </ColocaButton>
      </div>
    </ColocaSurface>
  );
}
